import {icon} from '#core/utils/style-utils.js';
import {merge} from '#core/utils/config-utils.js';
import {resolveBackground} from '#core/utils/color-utils.js';

import {registerTileSchema} from '#core/application/schema-registry.js';

import {generateEventTimingStatus} from '#core/application/modules/tiles/event-timing-status/index.js';
import {generateMiniScoreboard} from '#core/application/modules/tiles/mini-scoreboard/index.js';
import {generateBetNotifications} from '#core/application/modules/tiles/notification/bet-notifications/index.js';
import {
    generateAnimatedButton,
    generatePrimaryButton,
    generateSecondaryButton,
} from '#core/application/modules/tiles/button/index.js';
import {generateDotNotification} from '#core/application/modules/tiles/dot-notification/index.js';
import {generateBetBaseInfo} from '#core/application/modules/tiles/bet-base-info/index.js';
import {generateBorder} from '#core/application/modules/tiles/border/index.js';
import {generatePriceChangeIndicator} from '#core/application/modules/tiles/price-change-indicator/index.js';

import YOUR_BET from './your-bet.palette-schema.yaml';

registerTileSchema(YOUR_BET);

export function generateYourBet(TILE) {
    const YOUR_BET_BASE_INFO = generateBetBaseInfo(TILE.YOUR_BET_BASE_INFO);
    const YOUR_BET_CODE_BUTTON = generateSecondaryButton(TILE.YOUR_BET_CODE_BUTTON);

    return {
        container: {
            style: {
                ...resolveBackground(TILE.YOUR_BET_CONTAINER_BG),
                ...generateBorder(TILE.YOUR_BET_CONTAINER_BORDER),
                maxWidth: '100%',
                width: '100%',
            },
        },
        betInfoContainer: {
            style: {
                padding: '0 0 4px 20px',
            },
        },
        labels: {
            itemSpacing: 0,
            style: {
                minWidth: 0,
            },
        },
        betHeaderContainer: {
            itemSpacing: 0,
        },
        removeButton: {
            imageButton: {
                button: {
                    style: {
                        base: {
                            alignItems: 'start',
                        },
                    },
                },
                buttonIcon: icon(TILE.YOUR_BET_REMOVE_BET_BUTTON.CLOSE_BUTTON_ICON_COLOR, 'cross'),
            },
        },
        copyButton: {
            buttonIcon: icon(TILE.YOUR_BET_COPY_BET_BUTTON_ICON_COLOR),
        },
        // bet-funds info
        bonusIcon: YOUR_BET_BASE_INFO.betFundsInfo.medium.icon,
        freeBetIcon: YOUR_BET_BASE_INFO.betFundsInfo.medium.icon,
        totalStakeInfo: {
            betInfo: YOUR_BET_BASE_INFO.betFundsInfo.medium,
        },
        totalPayoutInfo: {
            payout: {
                betInfo: merge(YOUR_BET_BASE_INFO.betFundsInfo.medium, {
                    icon: {
                        style: {
                            minWidth: 16,
                            minHeight: 16,
                            _mobile_minHeight: '16px',
                            _mobile_minWidth: '16px',
                            _mobile_padding: '0 4px 0 0',
                        },
                    },
                }),
            },
            prewager: {
                cash: YOUR_BET_BASE_INFO.betFundsInfo.normal,
                bonus: YOUR_BET_BASE_INFO.betFundsInfo.normal,
            },
            tax: {
                betInfo: YOUR_BET_BASE_INFO.betFundsInfo.normal,
            },
            payoutAfterTax: {
                betInfo: merge(YOUR_BET_BASE_INFO.betFundsInfo.medium, {
                    icon: {
                        style: {
                            minWidth: 16,
                            minHeight: 16,
                        },
                    },
                }),
            },
        },
        oddsBoostInfo: {
            betInfo: YOUR_BET_BASE_INFO.betFundsInfo.normal,
        },
        betHeaderText: {
            style: YOUR_BET_BASE_INFO.title,
        },
        dateText: {
            style: YOUR_BET_BASE_INFO.date,
        },
        selectionName: {
            style: YOUR_BET_BASE_INFO.selectionName,
        },
        odds: {
            priceContainer: {
                style: {
                    ...YOUR_BET_BASE_INFO.odds,
                },
            },
            priceBoostIndicator: merge(YOUR_BET_BASE_INFO.priceBoostValue, {
                container: {
                    style: {
                        alignItems: 'baseline',
                    },
                },
            }),
            suspendedSelectionLabel: {
                suspendedText: {
                    container: {
                        ...YOUR_BET_BASE_INFO.suspendedNotification,
                    },
                },
            },
            updatedPrice: {
                style: {
                    ...YOUR_BET_BASE_INFO.oddsChangedValue.newValueContainer.style,
                    alignItems: 'flex-start',
                },
            },
        },
        betStatusText: {
            style: YOUR_BET_BASE_INFO.betStatus,
        },
        betStatusContainer: {
            style: {
                alignItems: 'center',
            },
        },
        suspendedMarket: {
            container: {
                ...YOUR_BET_BASE_INFO.suspendedNotification,
                padding: '2px 4px',
            },
        },
        eventName: {
            style: YOUR_BET_BASE_INFO.eventName,
        },
        marketName: {
            style: YOUR_BET_BASE_INFO.marketName,
        },
        matchAccaEventName: {
            style: YOUR_BET_BASE_INFO.betBuilderMarketName,
        },
        voidReasonMessage: merge(YOUR_BET_BASE_INFO.voidReasonNotification, {
            container: {
                style: {
                    padding: '0 0 4px',
                },
            },
        }),
        // leg-info-row
        legInfoRow: {
            eventTimingStatus: merge(generateEventTimingStatus(TILE.YOUR_BET_LIVE_EVENT_TIMING_STATUS), {
                dateTimeContainer: {
                    separator: {
                        style: {
                            color: TILE.YOUR_BET_LIVE_EVENT_DATA_SEPARATOR,
                        },
                    },
                },
            }),
            miniScoreboard: merge(generateMiniScoreboard(TILE.YOUR_BET_LIVE_EVENT_SCOREBOARD), {
                scoreColumn: {
                    margin: 0,
                },
                scoreCount: {
                    style: {
                        goalContainer: {
                            right: 'unset',
                            left: 0,
                        },
                    },
                },
            }),
            separator: {
                container: {
                    alignSelf: 'center',
                },
                style: {
                    color: TILE.YOUR_BET_LIVE_EVENT_DATA_SEPARATOR,
                    fontSize: 13,
                },
            },
            rowText: {
                style: {
                    lineHeight: 'initial',
                    ...TILE.YOUR_BET_RESULT_TEXT,
                },
            },
        },
        earlyPayoutIndicator: YOUR_BET_BASE_INFO.earlyPayoutIndicator,
        totalOdds: YOUR_BET_BASE_INFO.betTotalOdds,
        oddsBoostIndicator: {
            bonusIndicator: YOUR_BET_BASE_INFO.betBonusIndicator,
        },
        betBonusPromotions: {
            container: {
                style: {
                    padding: '4px 0',
                },
            },
            bonusIndicator: YOUR_BET_BASE_INFO.betBonusIndicator,
        },
        matchAccaSelectionItem: YOUR_BET_BASE_INFO.betBuilderSelectionItem,
        footerContainer: {
            style: {
                padding: '0 8px 8px',
            },
        },
        shareBetContainer: {
            style: {
                padding: FEATURES.betSharing ? '0 8px 0 0' : 0,
            },
        },
        shareBet: {
            shareButton: {
                button: {
                    style: {
                        base: {
                            ...TILE.YOUR_BET_SHARE_BET_BUTTON.TERTIARY_BUTTON_TEXT,
                        },
                    },
                },
                buttonIcon: {
                    style: {
                        fill: TILE.YOUR_BET_SHARE_BET_BUTTON.TERTIARY_BUTTON_ICON_COLOR,
                    },
                },
            },
        },
        betNotifications: generateBetNotifications(TILE.YOUR_BET_NOTIFICATIONS),
        // cash-out
        cashout: {
            buttonsContainer: {
                itemSpacing: 4,
            },
            cashoutButton: merge(generateAnimatedButton(TILE.YOUR_BET_CASH_OUT_BUTTON), {
                childrenContainer: {
                    itemSpacing: 2,
                },
            }),
            cashoutButtonText: {
                ellipsisOnLineNumber: 2,
            },
            cancelButtonLabel: {
                truncate: false,
            },
            cancelButton: merge(generateSecondaryButton(TILE.YOUR_BET_CASH_OUT_CANCEL_BUTTON), {
                style: {
                    base: {
                        maxWidth: 100,
                        minWidth: 'initial',
                    },
                },
            }),
            settingsButtonIcon: icon(TILE.YOUR_BET_CASH_OUT_SETTINGS_BUTTON.TERTIARY_BUTTON_ICON_COLOR),
            autoCashoutRuleDotNotification: generateDotNotification(
                TILE.YOUR_BET_CASH_OUT_SETTINGS_BUTTON_DOT_NOTIFICATION
            ),
            priceChangeIndicator: generatePriceChangeIndicator(TILE.YOUR_BET_CASH_OUT_PRICE_CHANGE_INDICATOR),
        },
        // retail
        betCodeContainer: {
            // honestly, I don't know, it looks the same as a secondary button
            style: YOUR_BET_CODE_BUTTON.style.base,
        },
        betDetailsButton: generatePrimaryButton(TILE.YOUR_BET_SHOW_DETAILS_BUTTON),
    };
}
